import { CategoryIcons, CategoryNames } from '../types';

// チュートリアルステップ定義
interface TutorialStep {
  id: string;
  title: string;
  content: string;
}

// チュートリアルセクション
interface TutorialSection {
  id: string;
  title: string;
  icon: string;
  steps: TutorialStep[];
}

// チュートリアル内容
const TUTORIAL_SECTIONS: TutorialSection[] = [
  {
    id: 'basics',
    title: '基本ルール',
    icon: '📖',
    steps: [
      {
        id: 'basics_1',
        title: 'Flag Battleとは?',
        content: `Flag Battleは「Challengers!」にインスパイアされたカードバトルゲームです。

🎯 目的: マッチで勝利して★(トロフィー)を集めよう!

7ラウンドの予選を経て、上位2名が決勝戦で対決します。`,
      },
      {
        id: 'basics_2',
        title: 'ゲームの流れ',
        content: `各ラウンドは2つのフェーズで構成:

1️⃣ デッキ構築フェーズ
   会場のカードプールから好きなカードを選択
   不要なカードは削除可能

2️⃣ マッチフェーズ
   構築したデッキで1対1のバトル!

これを7ラウンド繰り返します。`,
      },
      {
        id: 'basics_3',
        title: '勝敗とトロフィー',
        content: `🏆 マッチ勝利: ★(トロフィー)獲得
💔 マッチ敗北: ★を失う

★が多いほどランキング上位に!
上位2名が決勝戦へ進出します。`,
      },
    ],
  },
  {
    id: 'battle',
    title: 'マッチシステム',
    icon: '⚔️',
    steps: [
      {
        id: 'battle_1',
        title: 'フラッグ争奪戦',
        content: `マッチは「フラッグ」を巡る攻防です。

🚩 フラッグを持つ側 = 守備(DEFEND)
🗡️ フラッグを持たない側 = 攻撃(ATTACK)

攻撃側がフラッグを奪うか、守備側が守り切るかの勝負!`,
      },
      {
        id: 'battle_2',
        title: 'マッチの進行',
        content: `1. 先攻がデッキから1枚引いてフラッグを設置
2. 後攻(攻撃側)がカードを引く
3. 攻撃側の累積パワー >= フラッグのパワーでフラッグ奪取!
4. フラッグを奪われた側が攻撃側になる
5. 繰り返し...

⚠️ デッキが空になったら即敗北!`,
      },
      {
        id: 'battle_3',
        title: 'アタックスタック',
        content: `攻撃側は複数のカードを積み重ねられます。

📤 アタックスタック:
・引いたカードが積み重なる
・パワーは累積(合計値)
・フラッグ奪取時、一番上のカードが新フラッグに
・残りのカードは次に負けるまで残る`,
      },
      {
        id: 'battle_4',
        title: 'ベンチシステム',
        content: `フラッグを奪われたカードは「ベンチ」へ。

📦 ベンチ上限: 6枚
・同じカードは重なって1スロットに
・上限を超えると敗北!

🌟 ベンチ効果:
一部のカードはベンチにいる間、味方を強化します。`,
      },
    ],
  },
  {
    id: 'cards',
    title: 'カードについて',
    icon: '🃏',
    steps: [
      {
        id: 'cards_1',
        title: 'カードの見方',
        content: `各カードには以下の情報があります:

💪 パワー: バトルの強さ
   数字が大きいほど強い
   
🏷️ カテゴリー: カードの種類(7種類)

⭐ ティア: A/B/Cのランク
   ラウンドが進むと強いティアが出現

✨ 能力: 特殊効果(ないカードもある)`,
      },
      {
        id: 'cards_2',
        title: 'ティアシステム',
        content: `カードは3つのティアに分類:

🟢 Tier A (基本)
   パワー1〜3、序盤で多く出現

🔵 Tier B (中級)
   パワー3〜6、中盤から出現

🟣 Tier C (上級)
   パワー4〜10、終盤で少量出現

ラウンドが進むほど強いカードが増えます!`,
      },
      {
        id: 'cards_3',
        title: 'カテゴリー一覧',
        content: `${Object.entries(CategoryIcons).map(([cat, icon]) => 
          `${icon} ${CategoryNames[cat as keyof typeof CategoryNames]}`
        ).join('\n')}

各カテゴリーには特徴的な能力があります。
同じカテゴリーを集めるとシナジー効果が発動!`,
      },
    ],
  },
  {
    id: 'abilities',
    title: '能力について',
    icon: '✨',
    steps: [
      {
        id: 'abilities_1',
        title: '能力の発動タイミング',
        content: `能力には様々な発動タイミング:

🎯 プレイ時: カードを引いた時
🔍 ベンチ確認: プレイ時にベンチを参照
🌟 ベンチ時: ベンチにいる間ずっと有効
🚩 フラッグ取得時: フラッグを奪った時
👋 フラッグ喪失時: フラッグを奪われた時
📋 山札チェック: デッキを操作`,
      },
      {
        id: 'abilities_2',
        title: '主な能力効果',
        content: `⬆️ パワー強化: 自分や味方のパワーUP
🗑️ 除外: ベンチのカードを除外ゾーンへ
🔄 山札操作: デッキの順番を変更
🎴 カード追加: 山札からカードを追加
♻️ 復活: デッキに戻る
⭐ トロフィー: ★を獲得`,
      },
      {
        id: 'abilities_3',
        title: 'ベンチ効果の活用',
        content: `🌟ベンチ効果持ちカードが重要!

例: メイクアップアーティスト
「ベンチにある場合、映画属性+1」

同カテゴリーのカードを集めて
ベンチ効果で強化しよう!

※ベンチ効果持ちはベンチで光ります✨`,
      },
    ],
  },
  {
    id: 'deckbuilding',
    title: 'デッキ構築',
    icon: '🛠️',
    steps: [
      {
        id: 'deck_1',
        title: 'スターターデッキ選択',
        content: `ゲーム開始時に3つのデッキから選択:

⚔️ バランス型
   安定した構成、初心者向け

🔥 アグロ型
   攻撃的な構成、速攻重視

🛡️ コントロール型
   能力重視の構成、戦略的

プレイスタイルに合わせて選ぼう!`,
      },
      {
        id: 'deck_2',
        title: 'カードの追加',
        content: `各ラウンドのデッキ構築フェーズで:

1. 会場のカードプールが表示
2. 欲しいカードをタップして追加
3. 追加したカードはプールから消える
4. 制限時間内に構築完了

💡 カードは早い者勝ち!
   対戦相手と同じプールを共有しています。`,
      },
      {
        id: 'deck_3',
        title: 'カードの削除',
        content: `デッキからカードを削除することも重要!

📝 マイデッキをタップ
🗑️ 不要なカードを選択して削除

弱いカードを削除して、
デッキの平均パワーを上げよう!

⚠️ 削除したカードは除外され、
   カードプールには戻りません。`,
      },
      {
        id: 'deck_4',
        title: '構築のコツ',
        content: `🎯 カテゴリーを絞る
   シナジーを活かしやすい

🌟 ベンチ効果を重視
   強化効果で勝率UP

⚖️ パワーバランス
   高パワーと能力持ちを混ぜる

🗑️ 弱いカードは削除
   デッキの質を上げる`,
      },
    ],
  },
  {
    id: 'tips',
    title: '攻略のコツ',
    icon: '💡',
    steps: [
      {
        id: 'tips_1',
        title: '序盤の戦略',
        content: `🟢 ラウンド1-2

・Tier Aカードが中心
・ベンチ効果持ちを優先確保
・カテゴリーの方向性を決める
・スターターの弱いカードを削除`,
      },
      {
        id: 'tips_2',
        title: '中盤の戦略',
        content: `🔵 ラウンド3-5

・Tier Bカードが増加
・同カテゴリーを集めてシナジー強化
・パワー4以上のカードを確保
・不要なTier Aカードを削除`,
      },
      {
        id: 'tips_3',
        title: '終盤の戦略',
        content: `🟣 ラウンド6-7

・Tier Cの強力カードが出現
・パワー7以上のエースを確保
・デッキ枚数を絞って安定性UP
・決勝を見据えたデッキ完成`,
      },
      {
        id: 'tips_4',
        title: 'おすすめシナジー',
        content: `🎬 映画シナジー
   メイクアップ+ディレクターで大幅強化

🚀 宇宙シナジー
   バンドでパワーUP、AIでパワー2を強化

🏰 城シナジー
   鍛冶屋+吟遊詩人で全体強化

👻 幽霊シナジー
   除外で相手を妨害、復活で粘る`,
      },
    ],
  },
];

// チュートリアル完了状態を保存
const TUTORIAL_STORAGE_KEY = 'flagbattle_tutorial_completed';

export function isTutorialCompleted(): boolean {
  return localStorage.getItem(TUTORIAL_STORAGE_KEY) === 'true';
}

export function markTutorialCompleted(): void {
  localStorage.setItem(TUTORIAL_STORAGE_KEY, 'true');
}

export function resetTutorial(): void {
  localStorage.removeItem(TUTORIAL_STORAGE_KEY);
}

// チュートリアル画面をレンダリング
export function renderTutorialScreen(
  container: HTMLElement,
  onComplete: () => void
): void {
  let currentSectionIndex = 0;
  let currentStepIndex = 0;

  const render = () => {
    const section = TUTORIAL_SECTIONS[currentSectionIndex];
    const step = section.steps[currentStepIndex];
    const totalSteps = TUTORIAL_SECTIONS.reduce((sum, s) => sum + s.steps.length, 0);
    const currentGlobalStep = TUTORIAL_SECTIONS.slice(0, currentSectionIndex)
      .reduce((sum, s) => sum + s.steps.length, 0) + currentStepIndex + 1;

    container.innerHTML = `
      <div class="tutorial-screen">
        <style>
          .tutorial-screen {
            position: fixed;
            top: 0; left: 0; right: 0; bottom: 0;
            background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%);
            display: flex;
            flex-direction: column;
            padding: 20px;
            box-sizing: border-box;
            overflow: hidden;
          }
          
          .tutorial-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 16px;
            flex-shrink: 0;
          }
          
          .tutorial-title {
            font-size: 1.3rem;
            font-weight: bold;
            color: #00d4ff;
          }
          
          .tutorial-skip {
            background: rgba(255, 100, 100, 0.2);
            border: 1px solid rgba(255, 100, 100, 0.5);
            color: #ff6b6b;
            padding: 8px 16px;
            border-radius: 8px;
            cursor: pointer;
            font-size: 0.85rem;
            transition: all 0.3s;
          }
          
          .tutorial-skip:hover {
            background: rgba(255, 100, 100, 0.3);
          }
          
          .tutorial-nav {
            display: flex;
            gap: 8px;
            overflow-x: auto;
            padding-bottom: 8px;
            margin-bottom: 16px;
            flex-shrink: 0;
            -webkit-overflow-scrolling: touch;
          }
          
          .tutorial-nav-item {
            padding: 8px 12px;
            background: rgba(255,255,255,0.05);
            border-radius: 8px;
            font-size: 0.8rem;
            color: #888;
            cursor: pointer;
            white-space: nowrap;
            transition: all 0.3s;
          }
          
          .tutorial-nav-item:hover {
            background: rgba(255,255,255,0.1);
          }
          
          .tutorial-nav-item.active {
            background: rgba(0, 212, 255, 0.2);
            color: #00d4ff;
            border: 1px solid rgba(0, 212, 255, 0.5);
          }
          
          .tutorial-content {
            flex: 1;
            background: rgba(0,0,0,0.3);
            border-radius: 16px;
            padding: 20px;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            min-height: 0;
          }
          
          .tutorial-step-title {
            font-size: 1.2rem;
            font-weight: bold;
            color: #00ff88;
            margin-bottom: 16px;
            flex-shrink: 0;
          }
          
          .tutorial-step-content {
            flex: 1;
            overflow-y: auto;
            font-size: clamp(0.9rem, 3vw, 1rem);
            line-height: 1.8;
            color: #e0e0e0;
            white-space: pre-line;
            -webkit-overflow-scrolling: touch;
          }
          
          .tutorial-progress {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-top: 20px;
            padding-top: 16px;
            border-top: 1px solid rgba(255,255,255,0.1);
            flex-shrink: 0;
          }
          
          .tutorial-progress-bar {
            flex: 1;
            height: 8px;
            background: rgba(255,255,255,0.1);
            border-radius: 4px;
            overflow: hidden;
          }
          
          .tutorial-progress-fill {
            height: 100%;
            background: linear-gradient(90deg, #00d4ff, #00ff88);
            border-radius: 4px;
            transition: width 0.3s ease;
          }
          
          .tutorial-progress-text {
            font-size: 0.85rem;
            color: #888;
            white-space: nowrap;
          }
          
          .tutorial-buttons {
            display: flex;
            gap: 12px;
            margin-top: 16px;
            flex-shrink: 0;
          }
          
          .tutorial-btn {
            flex: 1;
            padding: 14px 20px;
            border: none;
            border-radius: 12px;
            font-size: 1rem;
            font-weight: bold;
            cursor: pointer;
            transition: all 0.3s;
          }
          
          .tutorial-btn-prev {
            background: rgba(255,255,255,0.1);
            color: #aaa;
          }
          
          .tutorial-btn-prev:hover {
            background: rgba(255,255,255,0.2);
            color: #fff;
          }
          
          .tutorial-btn-prev:disabled {
            opacity: 0.3;
            cursor: not-allowed;
          }
          
          .tutorial-btn-next {
            background: linear-gradient(135deg, #00d4ff, #00ff88);
            color: #000;
          }
          
          .tutorial-btn-next:hover {
            transform: scale(1.02);
            box-shadow: 0 4px 20px rgba(0, 212, 255, 0.4);
          }
          
          .tutorial-step-dots {
            display: flex;
            justify-content: center;
            gap: 8px;
            margin-top: 12px;
            flex-shrink: 0;
          }
          
          .tutorial-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: rgba(255,255,255,0.2);
            transition: all 0.3s;
          }
          
          .tutorial-dot.active {
            background: #00d4ff;
            transform: scale(1.2);
          }
          
          .tutorial-dot.completed {
            background: #00ff88;
          }
        </style>
        
        <div class="tutorial-header">
          <div class="tutorial-title">
            📚 チュートリアル
          </div>
          <button class="tutorial-skip" id="skipBtn">スキップ</button>
        </div>
        
        <div class="tutorial-nav">
          ${TUTORIAL_SECTIONS.map((s, i) => `
            <div class="tutorial-nav-item ${i === currentSectionIndex ? 'active' : ''}" 
                 data-section="${i}">
              ${s.icon} ${s.title}
            </div>
          `).join('')}
        </div>
        
        <div class="tutorial-content">
          <div class="tutorial-step-title">
            ${section.icon} ${step.title}
          </div>
          <div class="tutorial-step-content">
            ${step.content}
          </div>
          
          <div class="tutorial-step-dots">
            ${section.steps.map((_, i) => `
              <div class="tutorial-dot ${i === currentStepIndex ? 'active' : ''} ${i < currentStepIndex ? 'completed' : ''}"></div>
            `).join('')}
          </div>
        </div>
        
        <div class="tutorial-progress">
          <div class="tutorial-progress-bar">
            <div class="tutorial-progress-fill" style="width: ${(currentGlobalStep / totalSteps) * 100}%"></div>
          </div>
          <div class="tutorial-progress-text">${currentGlobalStep} / ${totalSteps}</div>
        </div>
        
        <div class="tutorial-buttons">
          <button class="tutorial-btn tutorial-btn-prev" id="prevBtn" 
                  ${currentSectionIndex === 0 && currentStepIndex === 0 ? 'disabled' : ''}>
            ◀ 前へ
          </button>
          <button class="tutorial-btn tutorial-btn-next" id="nextBtn">
            ${currentSectionIndex === TUTORIAL_SECTIONS.length - 1 && 
              currentStepIndex === section.steps.length - 1 
              ? '✓ 完了' : '次へ ▶'}
          </button>
        </div>
      </div>
    `;

    // イベントリスナー
    document.getElementById('skipBtn')?.addEventListener('click', () => {
      if (confirm('チュートリアルをスキップしますか?')) {
        markTutorialCompleted();
        onComplete();
      }
    });

    document.getElementById('prevBtn')?.addEventListener('click', () => {
      if (currentStepIndex > 0) {
        currentStepIndex--;
      } else if (currentSectionIndex > 0) {
        currentSectionIndex--;
        currentStepIndex = TUTORIAL_SECTIONS[currentSectionIndex].steps.length - 1;
      }
      render();
    });

    document.getElementById('nextBtn')?.addEventListener('click', () => {
      const section = TUTORIAL_SECTIONS[currentSectionIndex];
      if (currentStepIndex < section.steps.length - 1) {
        currentStepIndex++;
      } else if (currentSectionIndex < TUTORIAL_SECTIONS.length - 1) {
        currentSectionIndex++;
        currentStepIndex = 0;
      } else {
        markTutorialCompleted();
        onComplete();
        return;
      }
      render();
    });

    // セクションナビゲーション
    document.querySelectorAll('.tutorial-nav-item').forEach((el) => {
      el.addEventListener('click', () => {
        const sectionIndex = parseInt(el.getAttribute('data-section') || '0');
        currentSectionIndex = sectionIndex;
        currentStepIndex = 0;
        render();
      });
    });
  };

  render();
}

// クイックヘルプ
export function renderQuickHelp(container: HTMLElement, onClose: () => void): void {
  container.innerHTML = `
    <div class="quick-help-overlay">
      <style>
        .quick-help-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.9);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 20px;
          box-sizing: border-box;
        }
        
        .quick-help-content {
          background: linear-gradient(135deg, #1a1a2e, #16213e);
          border-radius: 16px;
          max-width: 500px;
          width: 100%;
          max-height: 80vh;
          overflow-y: auto;
          padding: 24px;
          -webkit-overflow-scrolling: touch;
        }
        
        .quick-help-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        
        .quick-help-title {
          font-size: 1.3rem;
          font-weight: bold;
          color: #00d4ff;
        }
        
        .quick-help-close {
          background: rgba(255,255,255,0.1);
          border: none;
          color: #fff;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          cursor: pointer;
          font-size: 1.2rem;
        }
        
        .quick-help-section {
          margin-bottom: 20px;
        }
        
        .quick-help-section-title {
          font-size: 1rem;
          font-weight: bold;
          color: #00ff88;
          margin-bottom: 8px;
        }
        
        .quick-help-section-content {
          font-size: 0.9rem;
          color: #ccc;
          line-height: 1.6;
        }
        
        .quick-help-btn {
          width: 100%;
          padding: 12px;
          background: linear-gradient(135deg, #00d4ff, #00ff88);
          border: none;
          border-radius: 8px;
          color: #000;
          font-weight: bold;
          cursor: pointer;
          margin-top: 16px;
        }
      </style>
      
      <div class="quick-help-content">
        <div class="quick-help-header">
          <div class="quick-help-title">❓ ヘルプ</div>
          <button class="quick-help-close" id="closeHelp">✕</button>
        </div>
        
        <div class="quick-help-section">
          <div class="quick-help-section-title">🎯 目的</div>
          <div class="quick-help-section-content">
            マッチで勝利して★(トロフィー)を集めよう!
            7ラウンドの予選後、上位2名が決勝へ。
          </div>
        </div>
        
        <div class="quick-help-section">
          <div class="quick-help-section-title">🛠️ デッキ構築</div>
          <div class="quick-help-section-content">
            カードをタップして追加。
            同じカテゴリーを集めてシナジー発動!
            不要なカードは削除して質を上げよう。
          </div>
        </div>
        
        <div class="quick-help-section">
          <div class="quick-help-section-title">⚔️ マッチ</div>
          <div class="quick-help-section-content">
            攻撃側の累積パワー >= フラッグのパワーで奪取!
            デッキが空になったら即敗北。
            フラッグを持っている状態で相手のデッキが尽きれば勝利!
          </div>
        </div>
        
        <div class="quick-help-section">
          <div class="quick-help-section-title">📦 ベンチ</div>
          <div class="quick-help-section-content">
            フラッグを奪われたカードはベンチへ。
            上限(6スロット)を超えると敗北!
            🌟付きカードはベンチで効果を発揮。
          </div>
        </div>
        
        <div class="quick-help-section">
          <div class="quick-help-section-title">💡 ヒント</div>
          <div class="quick-help-section-content">
            ・同カテゴリーを集めてベンチ効果活用
            ・弱いカードは削除してデッキ圧縮
            ・カードの詳細はタップで確認
          </div>
        </div>
        
        <button class="quick-help-btn" id="fullTutorialBtn">
          📚 フルチュートリアルを見る
        </button>
      </div>
    </div>
  `;

  document.getElementById('closeHelp')?.addEventListener('click', onClose);
  
  document.getElementById('fullTutorialBtn')?.addEventListener('click', () => {
    resetTutorial();
    onClose();
    location.reload();
  });
}
